import { useState, useEffect } from 'react'
import { toast } from 'sonner'
import { Key } from 'lucide-react'
import { PageHeader } from '@/components/layout/PageHeader'
import { FloatingActionIslandProvider } from '@/contexts/FloatingActionIslandContext'
import { FloatingActionIsland } from '@/components/ui/FloatingActionIsland'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { CopyButton } from '@/components/ui/CopyButton'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { CredentialsDialog } from '@/components/partner/CredentialsDialog'
import { useAppAuth } from '@/contexts/app-auth.context'
import { partnerClient } from '@/lib/api/partner.client'
import type {
  PartnerCredentials,
  GenerateCredentialsResponse,
} from '@/lib/api/partner.client'

const formatDate = (dateString: string | Date | null) => {
  if (!dateString) return 'Never'
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export function PartnerCredentialsPage() {
  const {
    isSuperAdmin,
    currentCompanyPartner,
    isCompanyLoading,
    isInitialized,
  } = useAppAuth()
  const [credentials, setCredentials] = useState<PartnerCredentials | null>(
    null,
  )
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [newSecret, setNewSecret] =
    useState<GenerateCredentialsResponse | null>(null)

  // Super admin viewing a partner company has read-only access
  const isReadOnly = isSuperAdmin && currentCompanyPartner !== null

  useEffect(() => {
    // Wait for auth to initialize and company/partner loading to complete
    if (!isInitialized || isCompanyLoading) return

    // For super admins, only fetch if viewing a partner company
    if (isSuperAdmin && !currentCompanyPartner) {
      setLoading(false)
      return
    }

    loadCredentials()
  }, [isSuperAdmin, currentCompanyPartner?.id, isCompanyLoading, isInitialized])

  const loadCredentials = async () => {
    try {
      setLoading(true)
      // Use admin endpoint if super admin viewing a partner company
      const data =
        isSuperAdmin && currentCompanyPartner
          ? await partnerClient.getCredentialsAsAdmin(currentCompanyPartner.id)
          : await partnerClient.getCredentials()
      setCredentials(data)
    } catch (error) {
      toast.error('Failed to load credentials')
    } finally {
      setLoading(false)
    }
  }

  const handleGenerateCredentials = async () => {
    if (
      credentials?.clientId &&
      !confirm(
        'This will revoke your existing credentials. Are you sure you want to continue?',
      )
    ) {
      return
    }

    try {
      setGenerating(true)
      const result = await partnerClient.generateCredentials()
      setNewSecret(result)
      await loadCredentials()
      toast.success('Credentials generated successfully')
    } catch (error) {
      toast.error('Failed to generate credentials')
    } finally {
      setGenerating(false)
    }
  }

  const handleCloseDialog = () => {
    setNewSecret(null)
  }

  return (
    <div className="h-full overflow-y-auto mt-16">
      <FloatingActionIslandProvider>
        <div className="space-y-6 p-6 pt-0">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mt-6">
            <PageHeader
              title={
                isReadOnly
                  ? 'View partner API credentials'
                  : 'Manage your API authentication credentials'
              }
            />
            {credentials?.clientId && !isReadOnly && (
              <Button
                onClick={handleGenerateCredentials}
                disabled={generating}
                variant="form"
                size="sm"
                className="h-9 w-fit shrink-0"
              >
                {generating ? 'Regenerating...' : 'Regenerate credentials'}
              </Button>
            )}
          </div>

          <CredentialsDialog
            open={!!newSecret}
            onOpenChange={handleCloseDialog}
            credentials={newSecret}
            title="Credentials Generated"
          />

          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="space-y-0">
                  <div className="border-b">
                    <div className="flex space-x-4 px-6 py-3.5">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-4 w-40" />
                      <Skeleton className="h-4 w-24" />
                    </div>
                  </div>
                  <div className="divide-y">
                    <div className="flex space-x-4 px-6 py-4">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-4 w-40" />
                      <Skeleton className="h-4 w-24" />
                    </div>
                  </div>
                </div>
              ) : !credentials?.clientId ? (
                <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                  <Key className="h-8 w-8 text-gray-400 mb-4" />
                  <h3 className="mb-2 text-lg font-semibold">
                    No credentials yet
                  </h3>
                  <p className="mb-6 max-w-sm text-sm text-muted-foreground">
                    {isReadOnly
                      ? 'This partner has not generated OAuth credentials yet'
                      : 'Generate OAuth credentials to authenticate with the Partner API'}
                  </p>
                  {!isReadOnly && (
                    <Button
                      onClick={handleGenerateCredentials}
                      disabled={generating}
                    >
                      Generate credentials
                    </Button>
                  )}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-1/2">Client ID</TableHead>
                      <TableHead>Client Secret</TableHead>
                      <TableHead>Generated</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <code className="font-mono text-sm">
                            {credentials.clientId}
                          </code>
                          <CopyButton
                            value={credentials.clientId}
                            variant="ghost"
                            size="sm"
                          />
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-muted-foreground text-sm font-mono">
                          {credentials.clientSecretHint || '••••••••••••'}
                        </span>
                      </TableCell>
                      <TableCell>
                        {formatDate(credentials.generatedAt)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <FloatingActionIsland />
        </div>
      </FloatingActionIslandProvider>
    </div>
  )
}
